import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Category as CategoryIcon,
  Phone as PhoneIcon,
  LocalOffer as DiscountIcon,
  Percent as PercentIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TICKET_TYPE from '../../../constants/ticketTypes';
import PAYMENT_STATUS, { getPaymentStatus } from '../../../constants/paymentStatus';
import { useSelector, useDispatch } from 'react-redux';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { generateRepairTicketPreview } from '../utils/ticketPreviewUtils';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import MultiplePaymentMethodsSection from '../../invoices/components/MultiplePaymentMethodsSection';
import { fetchProductCategories } from '../../../app/store/slices/productCategorySlice';
import { getCategoriesWithProducts } from '../../product/services/productService';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';
import { calcTotals as sharedCalcTotals, calcOriginalTotal } from '../utils/totalsCalculator';

// Strip HTML tags from template content
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').trim();
}

// Section Header Component
function SectionHeader({ icon: Icon, label, color = 'primary.main', action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
      <Icon sx={{ fontSize: 15, color }} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          fontSize: '0.63rem',
        }}
      >
        {label}
      </Typography>
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Box>
  );
}

function RepairTicketDialog({
  open,
  onClose,
  onSubmit,
  paymentMethods = [],
  customer,
  onOpenCustomerDialog,
  container,
  storeInfo: propStoreInfo,
  categoriesData = [], // ✅ Categories data from parent (POSListPage)
  categoryProductsCache = {}, // ✅ Cached products from parent
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { storeInfo: hookStoreInfo } = useStoreInfo();
  const storeInfo = propStoreInfo || hookStoreInfo;
  
  // Get product categories from Redux (for backward compatibility)
  const categoriesFromStore = useSelector((state) => state.productCategories.categories) || [];
  
  // Store categories with products locally
  const [categoriesWithProducts, setCategoriesWithProducts] = useState([]);
  const [allProductsMap, setAllProductsMap] = useState({});

  // Customer state
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Form state
  const [pickupDate, setPickupDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().slice(0, 10);
  });
  const [services, setServices] = useState([]);
  
  // Current service being added
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentService, setCurrentService] = useState({
    name: '',
    description: '',
    price: '',
    quantity: 1,
  });
  
  // Payment state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [localPMArray, setLocalPMArray] = useState([]);
  const [pmError, setPmError] = useState(false);
  
  // Notes template state
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedNoteTemplate, setSelectedNoteTemplate] = useState('');
  
  // Service validation state
  const [serviceError, setServiceError] = useState('');
  
  // Confirmation dialog state
  const [confirmUnpaidDialog, setConfirmUnpaidDialog] = useState({
    open: false,
    onConfirm: null,
  });

  // TVA and discount state
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);
  const [customVATRate, setCustomVATRate] = useState(21);
  const [discount, setDiscount] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountInputVal, setDiscountInputVal] = useState('');
  const [discountInputType, setDiscountInputType] = useState('percentage');

  // Preview state
  const [iframeHeight, setIframeHeight] = useState(600);
  const [receiptNumber, setReceiptNumber] = useState('');
  
  // Quotation mode state
  const [isQuotationMode, setIsQuotationMode] = useState(false);

  // Generate receipt number on open
  useEffect(() => {
    if (open) {
      const timestamp = Date.now();
      const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setReceiptNumber(`REP-${timestamp}-${randomPart}`);
    }
  }, [open]);
  
  // Fetch note templates and categories on open
  useEffect(() => {
    if (!open) return;
    
    // Skip if templates already loaded
    if (noteTemplates.length === 0) {
      documentNoteTemplateService.getTemplatesByType('RepairTicket')
        .then((data) => {
          const allTemplates = data?.payload || data || [];
          // Filter templates by DocumentType.RepairTicket (1)
          const templates = allTemplates.filter(t => 
            t.DocumentType === DocumentType.RepairTicket || 
            t.DocumentType === DocumentType.All
          );
          setNoteTemplates(templates);

          // Auto-select default or first template
          if (templates.length > 0) {
            const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
            setSelectedNoteTemplate(defaultTemplate.Id);
            const plainText = stripHtml(defaultTemplate.NoteContent);
            setNotes(plainText.slice(0, 1000));
          }
        })
        .catch(() => {});
    } else {
      // Templates already loaded, auto-select default or first template
      if (noteTemplates.length > 0) {
        const defaultTemplate = noteTemplates.find(t => t.IsDefault) || noteTemplates[0];
        setSelectedNoteTemplate(defaultTemplate.Id);
        const plainText = stripHtml(defaultTemplate.NoteContent);
        setNotes(plainText.slice(0, 1000));
      }
    }
    
    // ✅ Use categories data from parent (POSListPage) instead of making API call
    if (categoriesData && categoriesData.length > 0) {
      setCategoriesWithProducts(categoriesData);
      
      // Build a map of categoryId -> products from parent's cache
      const productsMap = {};
      categoriesData.forEach(category => {
        const categoryId = category.CategoryId || category.categoryId;
        const categoryProducts = category.Products || category.products || [];
        if (categoryId) {
          productsMap[categoryId] = categoryProducts;
        }
      });
      setAllProductsMap(productsMap);
      
      // Auto-select repair category if available (only on initial load)
      setTimeout(() => {
        const repairCategory = categoriesData.find(cat => {
          const name = (cat.CategoryName || cat.categoryName || '').toLowerCase();
          return name === 'repairation' || name === 'réparation' || name === 'herstel';
        });
        
        if (repairCategory) {
          const category = {
            Id: repairCategory.CategoryId || repairCategory.categoryId,
            Name: repairCategory.CategoryName || repairCategory.categoryName,
            ...repairCategory
          };
          setSelectedCategory(category);
        }
      }, 100); // Small delay to ensure reset effect completes first
    }
    
    // Also fetch categories for Redux (backward compatibility)
    if (categoriesFromStore.length === 0) {
      dispatch(fetchProductCategories());
    }
  }, [open, categoriesFromStore.length, dispatch, categoriesData]);

  // Auto-select template when dialog opens and templates are available
  useEffect(() => {
    if (open && noteTemplates.length > 0) {
      const defaultTemplate = noteTemplates.find(t => t.IsDefault) || noteTemplates[0];
      setSelectedNoteTemplate(defaultTemplate.Id);
      const plainText = stripHtml(defaultTemplate.NoteContent);
      setNotes(plainText.slice(0, 1000));
    }
  }, [open, noteTemplates]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const date = new Date();
      date.setDate(date.getDate() + 3);
      setPickupDate(date.toISOString().slice(0, 10));
      setServices([]);
      setSelectedCategory(null);
      setSelectedProduct(null);
      setCurrentService({ name: '', description: '', price: '', quantity: 1 });
      setSelectedPaymentMethod(null);
      setPaidAmount('');
      setServiceError(''); // Clear service error
      setIsTvaIncluded(true);
      setCustomVATRate(21);
      setDiscount(null);
      setShowDiscountForm(false);
      setDiscountInputVal('');
      setIframeHeight(600);
      setUseMultiplePayments(false);
      setLocalPMArray([]);
      setPmError(false);
      setIsQuotationMode(false);
      
      // Pre-fill customer info if customer is selected
      if (customer) {
        setCustomerName(customer.Name || '');
        setCustomerPhone(customer.PhoneNumber || customer.Phone || '');
      } else {
        setCustomerName('');
        setCustomerPhone('');
      }
    }
  }, [open, customer]);

  // Reset iframe height when services change
  useEffect(() => {
    setIframeHeight(600);
  }, [services.length]);

  // Get products for selected category
  const categoryProducts = useMemo(() => {
    if (!selectedCategory) return [];
    const categoryId = selectedCategory.Id || selectedCategory.id;
    
    // Get products from our local map
    const products = allProductsMap[categoryId] || [];
    
    return products;
  }, [selectedCategory, allProductsMap]);
  
  // Combine categories from both sources for display
  const availableCategories = useMemo(() => {
    // Use categoriesWithProducts if available, otherwise fall back to Redux categories
    if (categoriesWithProducts.length > 0) {
      return categoriesWithProducts.map(cat => ({
        Id: cat.CategoryId || cat.categoryId,
        Name: cat.CategoryName || cat.categoryName,
        ...cat
      }));
    }
    return categoriesFromStore;
  }, [categoriesWithProducts, categoriesFromStore]);

  // Calculate totals using shared calculator
  const { subtotal, discountAmount, totalVAT, totalIncl, subtotalExclVAT, originalTotal } = useMemo(() => {
    // Transform services to cart format for calculation
    const servicesAsCart = services.map(service => ({
      Quantity: parseInt(service.quantity) || 1,
      Price: parseFloat(service.price) || 0,
      PriceInclVAT: parseFloat(service.price) || 0,
      VAT: customVATRate
    }));
    
    const totals = sharedCalcTotals(servicesAsCart, discount, customVATRate, isTvaIncluded);
    
    return {
      subtotal: totals.subtotal,
      discountAmount: totals.discountAmount,
      totalVAT: totals.totalVAT,
      totalIncl: totals.totalIncl,
      subtotalExclVAT: totals.subtotal,
      originalTotal: totals.originalTotal
    };
  }, [services, discount, customVATRate, isTvaIncluded]);

  const paid = parseFloat(paidAmount) || 0;
  const remaining = Math.max(0, totalIncl - paid);

  // Calculate payment status using PAYMENT_STATUS constant
  const paymentStatusValue = getPaymentStatus(paid, totalIncl);
  const paymentStatus = {
    label: paymentStatusValue,
    color: paymentStatusValue === PAYMENT_STATUS.PAID ? 'success' :
           paymentStatusValue === PAYMENT_STATUS.PARTIAL_PAID ? 'warning' : 'error'
  };

  // Generate preview HTML
  const previewHtml = useMemo(() => {
    if (!storeInfo) {
      return `<html><body style="font-family:sans-serif;padding:40px 20px;text-align:center;color:#9e9e9e"><p style="font-size:14px">Loading preview...</p></body></html>`;
    }
    
    // Transform services to cart format (empty array if no services)
    // When VAT is included, convert price to base price (excl VAT)
    const cart = services.map(s => {
      const originalPrice = parseFloat(s.price) || 0;
      const basePrice = isTvaIncluded && originalPrice > 0 
        ? originalPrice / (1 + customVATRate / 100) 
        : originalPrice;
      
      return {
        Name: s.name,
        Description: s.description,
        Quantity: s.quantity,
        Price: basePrice,
        PriceInclVAT: isTvaIncluded ? originalPrice : undefined,
        VAT: customVATRate,
      };
    });

    return generateRepairTicketPreview({
      cart,
      customerName,
      customerPhone,
      storeInfo,
      receiptNumber,
      paidAmount,
      paymentMethod: selectedPaymentMethod,
      allPaymentMethods: paymentMethods,
      discount,
      customVATRate,
      notes: notes || '',
      pickupDate,
      isTvaIncluded,
      isQuotation: isQuotationMode,
    });
  }, [
    storeInfo, services, customer, customerName, customerPhone, selectedPaymentMethod,
    paidAmount, receiptNumber, discount, customVATRate, notes, paymentMethods, pickupDate, isTvaIncluded, isQuotationMode, t
  ]);

  // Handle category selection
  const handleCategoryChange = (event) => {
    setServiceError(''); // Clear error when changing category
    const categoryId = event.target.value;
    if (!categoryId) {
      setSelectedCategory(null);
      setSelectedProduct(null);
      setCurrentService({ name: '', description: '', price: '', quantity: 1 });
      return;
    }
    const category = availableCategories.find(c => (c.Id === categoryId || c.id === categoryId));
    setSelectedCategory(category);
    setSelectedProduct(null);
    // Reset all fields when category changes
    setCurrentService({ name: '', description: '', price: '', quantity: currentService.quantity });
  };

  // Handle product/service selection from dropdown
  const handleProductSelect = (event) => {
    const productId = event.target.value;
    setServiceError(''); // Clear error when selecting a product
    
    if (!productId) {
      // Cleared selection
      setSelectedProduct(null);
      setCurrentService({ ...currentService, name: '', description: '', price: '' });
      return;
    }
    
    // Find the selected product from category products
    // Handle both camelCase and PascalCase ID fields
    const product = categoryProducts.find(p => {
      const pId = p.Id || p.id || p.ProductId || p.productId;
      return pId === productId;
    });
    
    if (product) {
      setSelectedProduct(product);
      
      // âœ… Get the product price (this is the sale price from the database)
      const productPrice = parseFloat(product.SalePrice || product.salePrice || product.Price || product.price || 0);
      
      // âœ… Display the price as-is - the conversion to base price happens during preview generation
      // When isTvaIncluded=true, this shows VAT-included price
      // When isTvaIncluded=false, this shows VAT-excluded price
      setCurrentService({
        ...currentService,
        name: product.Name || product.name || '',
        description: product.Description || product.description || '',
        price: productPrice.toFixed(2),
      });
    }
  };
  
  // Handle manual input in service name (when no category selected)
  const handleServiceNameChange = (event) => {
    setCurrentService({ ...currentService, name: event.target.value });
    setServiceError('');
  };
  
  // Handle note template selection
  const handleNoteTemplateChange = (event) => {
    const templateId = event.target.value;
    setSelectedNoteTemplate(templateId);
    
    if (!templateId) {
      setNotes('');
      return;
    }
    
    const template = noteTemplates.find(t => t.Id === templateId);
    if (template) {
      const plainText = stripHtml(template.NoteContent);
      setNotes(plainText.slice(0, 1000));
    }
  };

  // Add service
  const handleAddService = () => {
    // Clear previous error
    setServiceError('');
    
    // Validate service name
    if (!currentService.name.trim()) {
      setServiceError(t('service_name_required') || 'Service name is required');
      return;
    }
    
    // Check for duplicate service name
    const isDuplicate = services.some(s => 
      s.name.toLowerCase().trim() === currentService.name.toLowerCase().trim()
    );
    if (isDuplicate) {
      setServiceError(t('service_already_added') || 'This service is already added to the list');
      return;
    }
    
    // Validate price (optional in quotation mode)
    if (!isQuotationMode && (!currentService.price || parseFloat(currentService.price) <= 0)) {
      setServiceError(t('service_price_required') || 'Service price is required');
      return;
    }

    const newService = {
      id: Date.now(),
      name: currentService.name.trim(),
      description: currentService.description.trim(),
      price: parseFloat(currentService.price) || 0, // Can be 0 in quotation mode
      quantity: parseInt(currentService.quantity) || 1,
    };

    setServices([...services, newService]);
    // Clear name, description, and price fields after adding
    setCurrentService({ name: '', description: '', price: '', quantity: 1 });
    setSelectedProduct(null);
  };

  // Remove service
  const handleRemoveService = (id) => {
    setServices(services.filter(s => s.id !== id));
  };

  // Update service quantity
  const handleUpdateQuantity = (id, quantity) => {
    setServices(services.map(s => 
      s.id === id ? { ...s, quantity: Math.max(1, parseInt(quantity) || 1) } : s
    ));
  };

  // Apply discount
  const handleApplyDiscount = () => {
    const value = parseFloat(discountInputVal);
    if (!value || value <= 0) {
      toast.error(t('invalid_discount') || 'Enter a valid discount value');
      return;
    }

    let amount = 0;
    if (discountInputType === 'percentage') {
      if (value > 100) {
        toast.error(t('discount_max_100') || 'Percentage cannot exceed 100%');
        return;
      }
      amount = (originalTotal * value) / 100;
    } else {
      amount = value;
      if (amount > originalTotal) {
        toast.error(t('discount_exceeds_total') || 'Discount cannot exceed total');
        return;
      }
    }

    setDiscount({ type: discountInputType, value, amount });
    setShowDiscountForm(false);
    setDiscountInputVal('');
    toast.success(t('discount_applied') || 'Discount applied');
  };

  // Remove discount
  const handleRemoveDiscount = () => {
    setDiscount(null);
    setDiscountInputVal('');
    toast.success(t('discount_removed') || 'Discount removed');
  };

  // Handle print quotation
  const handlePrintQuotation = () => {
    if (!customerName.trim()) {
      toast.error(t('customer_name_required') || 'Please enter customer name');
      return;
    }
    if (services.length === 0) {
      toast.error(t('at_least_one_service_required') || 'Please add at least one service');
      return;
    }
    if (!pickupDate) {
      toast.error(t('pickup_date_required') || 'Please select a pickup date');
      return;
    }
    
    // Open print dialog for quotation
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(previewHtml);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
      toast.success(t('quotation_ready_to_print') || 'Quotation is ready to print');
    } else {
      toast.error(t('popup_blocked') || 'Please allow popups to print quotation');
    }
  };
  
  // Handle submit
  const handleSubmit = async () => {
    // If in quotation mode, just print
    if (isQuotationMode) {
      handlePrintQuotation();
      return;
    }
    
    if (!customerName.trim()) {
      toast.error(t('customer_name_required') || 'Please enter customer name');
      return;
    }
    if (services.length === 0) {
      toast.error(t('at_least_one_service_required') || 'Please add at least one service');
      return;
    }
    if (!pickupDate) {
      toast.error(t('pickup_date_required') || 'Please select a pickup date');
      return;
    }
    
    // ✅ Validate payment: if paid amount > 0, payment method is required
    if (paid > 0 && !selectedPaymentMethod && !useMultiplePayments) {
      toast.error(t('payment_method_required') || 'Please select a payment method');
      setPmError(true);
      return;
    }

    // ✅ Warning for unpaid repair tickets
    if (paid === 0 || !paidAmount) {
      setConfirmUnpaidDialog({
        open: true,
        onConfirm: () => {
          setConfirmUnpaidDialog({ open: false, onConfirm: null });
          submitRepairTicket();
        },
      });
      return;
    }

    // If payment exists, submit directly
    submitRepairTicket();
  };

  // Separate function to handle actual submission
  const submitRepairTicket = async () => {
    const repairTicketData = {
      ticketType: TICKET_TYPE.REPAIR,
      receiptNumber,
      pickupDate: new Date(pickupDate).toISOString(),
      services: services.map(s => ({
        Name: s.name,
        Description: s.description,
        Quantity: s.quantity,
        Price: s.price,
        VAT: customVATRate,
      })),
      customer: customer || {
        Name: customerName,
        PhoneNumber: customerPhone,
      },
      customerName,
      customerPhone,
      // Only set payment method if there's a paid amount
      paymentMethod: paid > 0 ? selectedPaymentMethod : null,
      paidAmount: paid > 0 ? paid : null,
      notes: notes.trim(),
      subtotal,
      discountAmount,
      vatAmount: totalVAT,
      total: totalIncl,
      paymentStatus: paymentStatusValue,
      isTvaIncluded,
      customVATRate,
      discount,
      templateHtml: previewHtml,
      remainingAmount: remaining,
      // ✅ Add multiple payment support
      useMultiplePayments,
      paymentMethodsArray: localPMArray,
    };

    // âœ… Wait for submission to complete
    try {
      await onSubmit(repairTicketData);
      // âœ… Only show success and reset if submission succeeded
      // Note: onSubmit should throw an error if it fails
    
    // Reset form
      // Reset form
      setServices([]);
      setCurrentService({ name: '', description: '', price: '', quantity: 1 });
      setPaidAmount('');
      setNotes('');
      setCustomerName('');
      setCustomerPhone('');
      const date = new Date();
      date.setDate(date.getDate() + 3);
      setPickupDate(date.toISOString().slice(0, 10));
      setSelectedCategory(null);
      setSelectedProduct(null);
      setDiscount(null);
      setShowDiscountForm(false);
      setDiscountInputVal('');
      setUseMultiplePayments(false);
      setLocalPMArray([]);
      setPmError(false);
    } catch (error) {
      // Error is already handled in PosListPage, just log it
      console.error('âŒ Repair ticket submission error:', error);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().slice(0, 10);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => { 
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      container={container}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '92vh',
          height: isMobile ? '100%' : '92vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1500, // Higher z-index to appear above customer dialog
        },
      }}
      sx={{
        zIndex: 1500, // Ensure dialog itself has high z-index
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: 'white',
          px: 3,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BuildIcon sx={{ fontSize: 22 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: '1.05rem' }}>
              {t('create_repair_ticket') || 'Create Repair Ticket'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
              {t('repair_service_management') || 'Repair Service Management'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.15) } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Two-Panel Body */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* LEFT PANEL - Form */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 58%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            borderRight: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            bgcolor: 'white',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2.5,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#06b6d4', 0.2), borderRadius: 3 },
            }}
          >
            {/* Quotation Mode Toggle */}
            <Box sx={{ mb: 2 }}>
              <Paper
                elevation={isQuotationMode ? 3 : 1}
                sx={{
                  p: 1.5,
                  bgcolor: isQuotationMode ? '#fef3c7' : '#f0fdfa',
                  border: '2px solid',
                  borderColor: isQuotationMode ? '#f59e0b' : alpha('#06b6d4', 0.2),
                  borderRadius: 1.5,
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ fontSize: 18, color: isQuotationMode ? '#f59e0b' : '#06b6d4' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: isQuotationMode ? '#92400e' : '#0e7490' }}>
                        {isQuotationMode ? (t('quotation_mode') || 'Quotation Mode') : (t('repair_ticket_mode') || 'Repair Ticket Mode')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {isQuotationMode 
                          ? (t('quotation_mode_desc') || 'Prices optional • Print only')
                          : (t('repair_ticket_mode_desc') || 'Create repair ticket with payment')
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isQuotationMode}
                        onChange={(e) => {
                          setIsQuotationMode(e.target.checked);
                          if (e.target.checked) {
                            // Clear payment when switching to quotation mode
                            setPaidAmount('');
                            setSelectedPaymentMethod(null);
                            setUseMultiplePayments(false);
                            setLocalPMArray([]);
                            toast.info(t('quotation_mode_enabled') || 'Quotation mode enabled - prices are now optional');
                          }
                        }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#f59e0b',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#f59e0b',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        {t('quotation') || 'Quotation'}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Customer Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader icon={PersonIcon} label={t('customer_information') || 'Customer Info'} />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={<>{t('customer_name') || 'Customer Name'} <span style={{ color: '#ef4444' }}>*</span></>}
                    value={customerName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 20) {
                        setCustomerName(value);
                      }
                    }}
                    placeholder={t('enter_customer_name') || 'Enter name'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          {customerName && (
                            <IconButton
                              size="small"
                              onClick={() => setCustomerName('')}
                              sx={{ mr: 0.5 }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          {onOpenCustomerDialog && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={onOpenCustomerDialog}
                              sx={{ 
                                textTransform: 'none', 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.5,
                                borderColor: '#06b6d4',
                                color: '#06b6d4',
                                '&:hover': {
                                  borderColor: '#0891b2',
                                  bgcolor: alpha('#06b6d4', 0.05),
                                }
                              }}
                            >
                              {t('search_existing') || 'Search'}
                            </Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ maxLength: 20 }}
                    helperText={`${customerName.length}/20`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('customer_phone') || 'Phone Number'}
                    value={customerPhone}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numeric characters
                      if (/^\d*$/.test(value)) {
                        setCustomerPhone(value);
                      }
                    }}
                    placeholder="+32 xxx xx xx xx"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={<>{t('pickup_date') || 'Pickup Date'} <span style={{ color: '#ef4444' }}>*</span></>}
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                    }}
                    inputProps={{ min: today, max: maxDateStr }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Services Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader 
                icon={BuildIcon} 
                label={<>{t('services') || 'Services'} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span></>} 
                action={
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isTvaIncluded}
                        onChange={(e) => setIsTvaIncluded(e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#06b6d4',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#06b6d4',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                        {isTvaIncluded ? (t('vat_included') || 'VAT Incl.') : (t('vat_excluded') || 'VAT Excl.')}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                }
              />
              
              {/* Add Service Form */}
              <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5, border: '1px solid', borderColor: alpha('#06b6d4', 0.2) }}>
                {serviceError && (
                  <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fee2e2', borderRadius: 1, border: '1px solid #fca5a5' }}>
                    <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
                       {serviceError}
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('category') || 'Category (Optional)'}</InputLabel>
                      <Select
                        value={selectedCategory?.Id || selectedCategory?.id || ''}
                        onChange={handleCategoryChange}
                        label={t('category') || 'Category (Optional)'}
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryIcon sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        }
                        endAdornment={
                          selectedCategory && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCategory(null);
                                  setSelectedProduct(null);
                                  setCurrentService({ name: '', description: '', price: '', quantity: currentService.quantity });
                                }}
                                sx={{ mr: 0.5 }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                        MenuProps={{
                          sx: { zIndex: 1600 },
                          PaperProps: { sx: { zIndex: 1600 } }
                        }}
                      >
                        <MenuItem value=""><em>{t('none') || 'None'}</em></MenuItem>
                        {availableCategories && availableCategories.length > 0 ? (
                          availableCategories.map((cat) => (
                            <MenuItem key={cat.Id || cat.id} value={cat.Id || cat.id}>
                              {cat.Name || cat.name || cat.CategoryName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>{t('no_categories') || 'No categories available'}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    {selectedCategory ? (
                      <FormControl fullWidth size="small" key={`category-${selectedCategory?.Id || selectedCategory?.id}`}>
                        <InputLabel>{<>{t('service_name') || 'Service Name'} <span style={{ color: '#ef4444' }}>*</span></>}</InputLabel>
                        <Select
                          value={selectedProduct ? (selectedProduct.Id || selectedProduct.id || selectedProduct.ProductId || selectedProduct.productId || '') : ''}
                          onChange={handleProductSelect}
                          label={t('service_name') || 'Service Name'}
                          MenuProps={{
                            sx: { zIndex: 1600 },
                            PaperProps: { 
                              sx: { 
                                zIndex: 1600,
                                maxHeight: 400
                              } 
                            }
                          }}
                        >
                          <MenuItem value=""><em>{t('select_service') || 'Select a service'}</em></MenuItem>
                          {categoryProducts && categoryProducts.length > 0 ? (
                            categoryProducts.map((product) => {
                              const productId = product.Id || product.id || product.ProductId || product.productId;
                              return (
                                <MenuItem 
                                  key={productId} 
                                  value={productId}
                                  sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-start',
                                    py: 1.5
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {product.Name || product.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    €{(product.SalePrice || product.salePrice || product.Price || product.price || 0).toFixed(2)}
                                    {product.Description || product.description ? ` â€¢ ${(product.Description || product.description).substring(0, 60)}` : ''}
                                  </Typography>
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem disabled>{t('no_products_in_category') || 'No products in this category'}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label={<>{t('service_name') || 'Service Name'} <span style={{ color: '#ef4444' }}>*</span></>}
                        value={currentService.name}
                        onChange={handleServiceNameChange}
                        placeholder={t('type_service_name') || 'Type service name'}
                        InputProps={{
                          endAdornment: currentService.name && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setCurrentService({ ...currentService, name: '' })}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('description') || 'Description (optional)'}
                      value={currentService.description}
                      onChange={(e) => setCurrentService({ ...currentService, description: e.target.value })}
                      InputProps={{
                        endAdornment: currentService.description && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setCurrentService({ ...currentService, description: '' })}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={
                        <>
                          {isTvaIncluded ? (t('price_incl_vat') || 'Price (incl. VAT)') : (t('price_excl_vat') || 'Price (excl. VAT)')}
                          {!isQuotationMode && <span style={{ color: '#ef4444' }}> *</span>}
                          {isQuotationMode && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: 4 }}>(Optional)</span>}
                        </>
                      }
                      value={currentService.price}
                      onChange={(e) => {
                        setCurrentService({ ...currentService, price: e.target.value });
                        setServiceError(''); // Clear error when typing
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder={isQuotationMode ? (t('optional') || 'Optional') : ''}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('quantity') || 'Qty'}
                      value={currentService.quantity}
                      onChange={(e) => setCurrentService({ ...currentService, quantity: e.target.value })}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddService}
                      sx={{
                        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                        height: '40px',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                        },
                      }}
                    >
                      {t('add') || 'Add'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Services List */}
              {services.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0fdfa' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{t('service') || 'Service'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{t('qty') || 'Qty'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{t('price') || 'Price'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{t('total') || 'Total'}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', width: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {service.name}
                            </Typography>
                            {service.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {service.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={service.quantity}
                              onChange={(e) => handleUpdateQuantity(service.id, e.target.value)}
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 50 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>€{service.price.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            €{(service.price * service.quantity).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleRemoveService(service.id)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('no_services_added') || 'No services added yet'}
                  </Typography>
                </Paper>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Payment Section - Hidden in quotation mode */}
            {!isQuotationMode && (
              <Box sx={{ mb: 2.5 }}>
                <SectionHeader icon={PaymentIcon} label={t('payment') || 'Payment'} />
              
              {/* Multi-payment toggle */}
              <Box sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useMultiplePayments}
                      onChange={(e) => {
                        setUseMultiplePayments(e.target.checked);
                        if (e.target.checked) {
                          setSelectedPaymentMethod(null);
                          setPmError(false);
                        } else {
                          setLocalPMArray([]);
                        }
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#06b6d4',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: '#06b6d4',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {t('multi_payment') || 'Multiple Payment Methods'}
                    </Typography>
                  }
                  sx={{ mb: 0 }}
                />
              </Box>

              {!useMultiplePayments ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('paid_amount') || 'Paid Amount'}
                      value={paidAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPaidAmount(value);
                        // Clear payment method if amount is cleared or set to 0
                        if (!value || parseFloat(value) === 0) {
                          setSelectedPaymentMethod(null);
                          setPmError(false);
                        }
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText={
                        paid > 0 && remaining > 0
                          ? `${t('remaining') || 'Remaining'}: €${remaining.toFixed(2)}`
                          : paid > 0 && remaining <= 0
                          ? `${t('fully_paid') || 'Fully Paid'}`
                          : `${t('total') || 'Total'}: €${totalIncl.toFixed(2)}`
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" error={pmError} disabled={!paidAmount || parseFloat(paidAmount) === 0}>
                      <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                      <Select
                        value={selectedPaymentMethod?.id || ''}
                        onChange={(e) => {
                          const pm = paymentMethods.find(p => p.id === e.target.value);
                          setSelectedPaymentMethod(pm);
                          setPmError(false);
                        }}
                        label={t('payment_method') || 'Payment Method'}
                        disabled={!paidAmount || parseFloat(paidAmount) === 0}
                        MenuProps={{
                          sx: { zIndex: 1600 },
                          PaperProps: { sx: { zIndex: 1600 } }
                        }}
                      >
                        {paymentMethods.map((pm) => (
                          <MenuItem key={pm.id} value={pm.id}>
                            {pm.label || pm.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {(!paidAmount || parseFloat(paidAmount) === 0) && (
                        <FormHelperText>{t('enter_amount_first') || 'Enter payment amount first'}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('vat_rate') || 'VAT Rate (%)'}
                      value={customVATRate}
                      onChange={(e) => setCustomVATRate(parseFloat(e.target.value) || 21)}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {discount ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, height: '40px' }}>
                        <DiscountIcon sx={{ fontSize: 18, color: '#059669' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value.toFixed(2)}`} {t('discount') || 'Discount'}
                          </Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={handleRemoveDiscount}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<DiscountIcon />}
                        onClick={() => setShowDiscountForm(!showDiscountForm)}
                        sx={{ height: '40px', textTransform: 'none' }}
                      >
                        {t('add_discount') || 'Add Discount'}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <MultiplePaymentMethodsSection
                    paymentMethods={localPMArray}
                    onPaymentMethodsChange={setLocalPMArray}
                    totalAmount={totalIncl}
                    disabled={totalIncl <= 0}
                    paymentMethodOptions={paymentMethods.map((pm) => ({ value: pm.id, label: pm.label || pm.name }))}
                  />
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('vat_rate') || 'VAT Rate (%)'}
                        value={customVATRate}
                        onChange={(e) => setCustomVATRate(parseFloat(e.target.value) || 21)}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {discount ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, height: '40px' }}>
                          <DiscountIcon sx={{ fontSize: 18, color: '#059669' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value.toFixed(2)}`} {t('discount') || 'Discount'}
                            </Typography>
                          </Box>
                          <IconButton size="small" color="error" onClick={handleRemoveDiscount}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DiscountIcon />}
                          onClick={() => setShowDiscountForm(!showDiscountForm)}
                          sx={{ height: '40px', textTransform: 'none' }}
                        >
                          {t('add_discount') || 'Add Discount'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Discount Form */}
              {showDiscountForm && !discount && (
                <Paper sx={{ p: 1.5, mt: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={discountInputType}
                        exclusive
                        onChange={(e, newType) => newType && setDiscountInputType(newType)}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="percentage">
                          <PercentIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('percentage') || 'Percentage'}
                        </ToggleButton>
                        <ToggleButton value="fixed">€ {t('fixed') || 'Fixed'}</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={discountInputType === 'percentage' ? t('discount_percentage') || 'Discount %' : t('discount_amount') || 'Amount'}
                        value={discountInputVal}
                        onChange={(e) => setDiscountInputVal(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{discountInputType === 'percentage' ? '%' : '€'}</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: discountInputType === 'percentage' ? 1 : 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApplyDiscount}
                        sx={{ 
                          height: '40px',
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        }}
                      >
                        {t('apply') || 'Apply'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Notes Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader icon={NotesIcon} label={t('notes') || 'Notes'} />
              <Paper sx={{ p: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5, border: '1px solid', borderColor: alpha('#06b6d4', 0.2) }}>
                <Grid container spacing={1.5}>
                {noteTemplates.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('note_template') || 'Note Template'}</InputLabel>
                      <Select
                        value={selectedNoteTemplate}
                        onChange={handleNoteTemplateChange}
                        label={t('note_template') || 'Note Template'}
                        MenuProps={{
                          sx: { zIndex: 1600 },
                          PaperProps: { sx: { zIndex: 1600 } }
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('none') || 'None'}</em>
                        </MenuItem>
                        {noteTemplates.map((template) => (
                          <MenuItem key={template.Id} value={template.Id}>
                            {template.TemplateName}
                            {template.IsDefault && ` (Default)`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label={t('notes_optional') || 'Notes (optional)'}
                    value={notes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        setNotes(value);
                      }
                    }}
                    placeholder={t('enter_custom_notes') || 'Enter custom notes here...'}
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${notes.length}/1000`}
                  />
                </Grid>
              </Grid>
              </Paper>
            </Box>
          </Box>

          {/* Bottom Action Buttons */}
          <Box sx={{ flexShrink: 0, p: 2.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onClose}
                  sx={{ py: 1.25 }}
                >
                  {t('cancel') || 'Cancel'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isQuotationMode ? <ReceiptIcon /> : <BuildIcon />}
                  onClick={handleSubmit}
                  disabled={services.length === 0 || !customerName.trim()}
                  sx={{
                    py: 1.25,
                    background: isQuotationMode 
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    '&:hover': {
                      background: isQuotationMode
                        ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                        : 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                    },
                  }}
                >
                  {isQuotationMode ? (t('print_quotation') || 'Print Quotation') : (t('create') || 'Create')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* RIGHT PANEL - Preview */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 42%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#dde1e7',
            minHeight: isMobile ? 300 : 0,
          }}
        >
          {/* Preview Title */}
          <Box
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 0.75,
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ReceiptIcon sx={{ fontSize: 14, color: '#06b6d4' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                {t('ticket_preview') || 'Ticket Preview'}
              </Typography>
            </Box>
          </Box>

          {/* Preview Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#06b6d4', 0.25), borderRadius: 2 },
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: '302px',
                bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <iframe
                key={`${services.length}-${discountAmount}`}
                title="ticket-preview"
                srcDoc={previewHtml}
                scrolling="no"
                style={{
                  width: '302px',
                  height: iframeHeight,
                  border: 'none',
                  display: 'block',
                }}
                onLoad={(e) => {
                  const iframe = e.target;
                  setTimeout(() => {
                    try {
                      const doc = iframe.contentDocument;
                      const container = doc.querySelector('.receipt-container');
                      const bodyPad = 20;
                      const h = container
                        ? container.offsetHeight + bodyPad
                        : Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
                      if (h > 100) setIframeHeight(h + 32);
                    } catch {
                      setIframeHeight(Math.max(800, services.length * 80 + 600));
                    }
                  }, 300);
                }}
                sandbox="allow-same-origin"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Unpaid Repair Ticket Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmUnpaidDialog.open}
        title={t('confirm_unpaid_ticket') || 'Confirm Unpaid Repair Ticket'}
        message={
          t('confirm_unpaid_repair_ticket_detailed') ||
          `⚠️ No payment recorded for this repair ticket.\n\nThe ticket will be created with status "${PAYMENT_STATUS.NOT_PAID}" (€${totalIncl.toFixed(2)} due).\n\nCustomer: ${customerName}\nPickup Date: ${new Date(pickupDate).toLocaleDateString()}\n\nDo you want to proceed without payment?`
        }
        onConfirm={confirmUnpaidDialog.onConfirm}
        onCancel={() => {
          setConfirmUnpaidDialog({ open: false, onConfirm: null });
          toast.info(t('operation_cancelled') || 'Operation cancelled. Please add payment details.');
        }}
        confirmText={t('proceed_without_payment') || 'Proceed Without Payment'}
        cancelText={t('cancel') || 'Cancel'}
        variant="warning"
        container={container}
        zIndex={1600}
      />
    </Dialog>
  );
}

export default RepairTicketDialog;